'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { getEvents } from '@/app/actions/event'
import {
  DEFAULT_EVENT_NEAR_RADIUS_MILES,
  EVENT_NEAR_FETCH_LIMIT,
  milesToMeters,
  parseEventNearRadiusMiles,
} from '@/constants/eventNearRadius'
import { useDebounce } from '@/hooks/useDebounce'
import { toDisplayEvents } from '@/utilities/events/displayEvent'

import type { EventNearRadiusMiles } from '@/constants/eventNearRadius'
import type { Topic } from '@/payload-types'
import type { DisplayEvent } from '@/utilities/events/displayEvent'

type EventView = 'all' | 'official' | 'unofficial'
type EventFormat = 'all' | 'in-person' | 'virtual'

export type EventSearchLocation = {
  lat: number
  lng: number
  label?: string
}

const parseEventView = (value: string | null): EventView => {
  if (value === 'official' || value === 'unofficial') return value
  return 'all'
}

const parseEventFormat = (value: string | null): EventFormat => {
  if (value === 'in-person' || value === 'virtual') return value
  return 'all'
}

const parseTopicsFromParam = (value: string | null, topics: Topic[]): Topic[] => {
  if (!value) return []
  const slugs = value
    .split(',')
    .map((slug) => slug.trim())
    .filter(Boolean)
  return slugs
    .map((slug) => topics.find((topic) => topic.slug === slug))
    .filter((topic): topic is Topic => Boolean(topic))
}

const topicsToParam = (topics: Topic[]): string | null => {
  if (topics.length === 0) return null
  return topics.map((topic) => topic.slug).join(',')
}

const parseSearchLocation = (
  latParam: string | null,
  lngParam: string | null,
  labelParam: string | null,
): EventSearchLocation | null => {
  if (latParam == null || lngParam == null) return null
  const lat = Number(latParam)
  const lng = Number(lngParam)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return {
    lat,
    lng,
    ...(labelParam ? { label: labelParam } : {}),
  }
}

interface EventsContextType {
  featured?: DisplayEvent | null

  // Data
  events: DisplayEvent[]
  eventTopicData: (Topic & { count: number })[]

  // Current page data (paginated at source)
  totalDocs: number
  page: number
  setPage: (page: number) => void
  isLoading: boolean

  // Topics filter
  selectedTopics: Topic[]
  setSelectedTopics: (topics: Topic[]) => void
  toggleTopic: (topic: Topic) => void
  clearTopics: () => void
  filterOpen: boolean
  setFilterOpen: (filterOpen: boolean) => void

  // Search
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void

  // Location search
  searchLocation: EventSearchLocation | null
  setSearchLocation: (location: EventSearchLocation | null) => void
  clearSearchLocation: () => void
  searchThisArea: (lat: number, lng: number) => void
  radiusMiles: EventNearRadiusMiles
  setRadiusMiles: (miles: EventNearRadiusMiles) => void

  // Sorting
  sortBy: string
  setSortBy: (sortBy: string) => void
  sortDirection: 'asc' | 'desc'
  setSortDirection: (direction: 'asc' | 'desc') => void
  toggleSortDirection: () => void

  // View
  view: EventView
  setView: (view: EventView) => void

  // Format
  format: EventFormat
  setFormat: (format: EventFormat) => void
}

const EventsContext = createContext<EventsContextType | undefined>(undefined)

export const useEventsContext = () => {
  const context = useContext(EventsContext)
  if (!context) {
    throw new Error('useEventsContext must be used within a EventsProvider')
  }
  return context
}

type EventsProviderProps = {
  children: React.ReactNode
  featured?: DisplayEvent | null
  initialEvents: DisplayEvent[]
  initialTotalDocs: number
  eventTopicsByView: Record<EventView, Topic[]>
  eventTopicCountsByView: Record<EventView, { [key: number]: number }>
}

export const EventsProvider = ({
  children,
  featured,
  initialEvents,
  initialTotalDocs,
  eventTopicsByView,
  eventTopicCountsByView,
}: EventsProviderProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const currentParams = useRef(searchParams.toString())
  const skipSearchParamSync = useRef(true)

  const initialView = parseEventView(searchParams.get('class'))
  const initialFormat = parseEventFormat(searchParams.get('venue'))
  const initialTopics = parseTopicsFromParam(
    searchParams.get('topics'),
    eventTopicsByView[initialView] ?? eventTopicsByView.all ?? [],
  )
  const initialLocation = parseSearchLocation(
    searchParams.get('lat'),
    searchParams.get('lng'),
    searchParams.get('loc'),
  )
  const initialRadius = parseEventNearRadiusMiles(searchParams.get('radius'))

  const [events, setEvents] = useState<DisplayEvent[]>(initialEvents)
  const [totalDocs, setTotalDocs] = useState(initialTotalDocs)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>(initialTopics)
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') ?? '')
  const [searchLocation, setSearchLocationState] = useState<EventSearchLocation | null>(
    initialFormat === 'virtual' ? null : initialLocation,
  )
  const [radiusMiles, setRadiusMilesState] = useState<EventNearRadiusMiles>(initialRadius)
  const [sortBy, setSortBy] = useState('general.startAt')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [view, setViewState] = useState<EventView>(initialView)
  const [format, setFormatState] = useState<EventFormat>(initialFormat)
  const [filterOpen, setFilterOpen] = useState(false)
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const currentViewTopics = useMemo(() => {
    return eventTopicsByView[view] ?? []
  }, [eventTopicsByView, view])

  const currentViewTopicCounts = useMemo(() => {
    return eventTopicCountsByView[view] ?? {}
  }, [eventTopicCountsByView, view])

  const eventTopicData = useMemo(() => {
    return currentViewTopics.map((topic) => ({
      ...topic,
      count: currentViewTopicCounts[topic.id] ?? 0,
    }))
  }, [currentViewTopicCounts, currentViewTopics])

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(
        typeof window !== 'undefined' ? window.location.search : currentParams.current,
      )
      let changed = false

      for (const [key, value] of Object.entries(updates)) {
        if (newParams.get(key) === value) continue
        if (value === null) {
          newParams.delete(key)
        } else {
          newParams.set(key, value)
        }
        changed = true
      }

      if (!changed) return

      currentParams.current = newParams.toString()
      const url = currentParams.current ? `${pathname}?${currentParams.current}` : pathname

      window.history.replaceState(window.history.state, '', url)
      router.replace(url, { scroll: false })
    },
    [pathname, router],
  )

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      updateParams({ [key]: value })
    },
    [updateParams],
  )

  useEffect(() => {
    currentParams.current = searchParams.toString()
  }, [searchParams])

  useEffect(() => {
    const applyFromUrl = () => {
      const params = new URLSearchParams(window.location.search)
      currentParams.current = params.toString()

      const urlView = parseEventView(params.get('class'))
      const urlFormat = parseEventFormat(params.get('venue'))
      const urlSearch = params.get('q') ?? ''
      const urlTopics = parseTopicsFromParam(
        params.get('topics'),
        eventTopicsByView[urlView] ?? eventTopicsByView.all ?? [],
      )
      const urlLocation = parseSearchLocation(
        params.get('lat'),
        params.get('lng'),
        params.get('loc'),
      )
      const urlRadius = parseEventNearRadiusMiles(params.get('radius'))

      setViewState(urlView)
      setFormatState(urlFormat)
      setSearchTerm(urlSearch)
      setSelectedTopics(urlTopics)
      setSearchLocationState(urlFormat === 'virtual' ? null : urlLocation)
      setRadiusMilesState(urlRadius)
      setPage(1)
    }

    window.addEventListener('popstate', applyFromUrl)
    return () => window.removeEventListener('popstate', applyFromUrl)
  }, [eventTopicsByView])

  useEffect(() => {
    if (skipSearchParamSync.current) {
      skipSearchParamSync.current = false
      return
    }
    const trimmed = debouncedSearchTerm.trim()
    updateParam('q', trimmed.length > 0 ? trimmed : null)
  }, [debouncedSearchTerm, updateParam])

  const setSearchLocation = useCallback(
    (location: EventSearchLocation | null) => {
      setSearchLocationState(location)
      setPage(1)
      if (!location) {
        updateParams({
          lat: null,
          lng: null,
          loc: null,
          radius: null,
        })
        setRadiusMilesState(DEFAULT_EVENT_NEAR_RADIUS_MILES)
        return
      }
      updateParams({
        lat: String(location.lat),
        lng: String(location.lng),
        loc: location.label?.trim() ? location.label.trim() : null,
        radius: String(radiusMiles),
      })
    },
    [radiusMiles, updateParams],
  )

  const clearSearchLocation = useCallback(() => {
    setSearchLocation(null)
  }, [setSearchLocation])

  const searchThisArea = useCallback(
    (lat: number, lng: number) => {
      setSearchLocation({
        lat,
        lng,
        label: searchLocation?.label,
      })
    },
    [searchLocation?.label, setSearchLocation],
  )

  const setRadiusMiles = useCallback(
    (miles: EventNearRadiusMiles) => {
      setRadiusMilesState(miles)
      setPage(1)
      if (searchLocation) {
        updateParams({
          lat: String(searchLocation.lat),
          lng: String(searchLocation.lng),
          loc: searchLocation.label?.trim() ? searchLocation.label.trim() : null,
          radius: String(miles),
        })
      } else {
        updateParam('radius', miles === DEFAULT_EVENT_NEAR_RADIUS_MILES ? null : String(miles))
      }
    },
    [searchLocation, updateParam, updateParams],
  )

  const setView = useCallback(
    (nextView: EventView) => {
      setViewState(nextView)
      setPage(1)
      setSelectedTopics([])
      updateParams({
        class: nextView === 'all' ? null : nextView,
        topics: null,
      })
    },
    [updateParams],
  )

  const setFormat = useCallback(
    (nextFormat: EventFormat) => {
      setFormatState(nextFormat)
      setPage(1)
      if (nextFormat === 'virtual') {
        setSearchLocationState(null)
        setRadiusMilesState(DEFAULT_EVENT_NEAR_RADIUS_MILES)
        updateParams({
          venue: 'virtual',
          lat: null,
          lng: null,
          loc: null,
          radius: null,
        })
        return
      }
      updateParams({ venue: nextFormat === 'all' ? null : nextFormat })
    },
    [updateParams],
  )

  useEffect(() => {
    // Guard against out-of-order responses: e.g. the initial nationwide fetch
    // resolving after a "use current location" fetch and overwriting it.
    let ignore = false

    async function fetchEvents() {
      setIsLoading(true)

      const near =
        searchLocation && format !== 'virtual'
          ? {
              lat: searchLocation.lat,
              lng: searchLocation.lng,
              maxDistanceMeters: milesToMeters(radiusMiles),
            }
          : undefined

      // High limit so the always-on map can show national or radius pins (not
      // just one list page). The grid paginates the fetched set client-side.
      const eventsRes = await getEvents({
        search: debouncedSearchTerm,
        limit: EVENT_NEAR_FETCH_LIMIT,
        sort: `${sortDirection === 'desc' ? '-' : ''}${sortBy}`,
        topics: selectedTopics.map((topic) => topic.id),
        view: view === 'all' ? undefined : view,
        format: format === 'all' ? undefined : format,
        near,
      })
      if (ignore) return
      if (eventsRes === null) {
        setEvents([])
        setTotalDocs(0)
        setIsLoading(false)
        return
      }
      const displayEvents = toDisplayEvents(eventsRes)
      setEvents(displayEvents)
      setTotalDocs(displayEvents.length)
      setIsLoading(false)
    }

    void fetchEvents()

    return () => {
      ignore = true
    }
  }, [
    debouncedSearchTerm,
    selectedTopics,
    sortBy,
    sortDirection,
    view,
    format,
    searchLocation,
    radiusMiles,
  ])

  const toggleTopic = useCallback(
    (topic: Topic) => {
      setSelectedTopics((prev) => {
        const exists = prev.some((t) => t.id === topic.id)
        const next = exists ? prev.filter((t) => t.id !== topic.id) : [...prev, topic]
        updateParam('topics', topicsToParam(next))
        return next
      })
    },
    [updateParam],
  )

  const clearTopics = useCallback(() => {
    setSelectedTopics([])
    updateParam('topics', null)
  }, [updateParam])

  const toggleSortDirection = useCallback(() => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }, [])

  return (
    <EventsContext.Provider
      value={{
        featured,
        events,
        eventTopicData,
        totalDocs,
        page,
        setPage,
        isLoading,
        selectedTopics,
        setSelectedTopics,
        toggleTopic,
        clearTopics,
        filterOpen,
        setFilterOpen,
        searchTerm,
        setSearchTerm,
        searchLocation,
        setSearchLocation,
        clearSearchLocation,
        searchThisArea,
        radiusMiles,
        setRadiusMiles,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        toggleSortDirection,
        view,
        setView,
        format,
        setFormat,
      }}
    >
      {children}
    </EventsContext.Provider>
  )
}
